import PortalIdParser from 'PortalIdParser';
import { isUnsafeUrl } from 'UIComponents/utils/UnsafeUrl';

// @ts-ignore URLParts not typed
import UrlParts from 'PatternValidationJS/regex/parts/UrlParts';
export const URL_REGEX_WITH_REQUIRED_PROTOCOL_REGEX = new RegExp(`^(${UrlParts.protocol})?${UrlParts.domain}(?:${UrlParts.path}(?:${UrlParts.file})?)?${UrlParts.search}?$`, 'i');
export const URL_REGEX_WITHOUT_PROTOCOL_REGEX = new RegExp(`${UrlParts.domain}(?:${UrlParts.path}(?:${UrlParts.file})?)?${UrlParts.search}?$`, 'i');
const PORTAL_ID_FROM_PATH_REGEX = /\/(?:[A-Za-z0-9-_]*)\/(\d+)(?:\/|$)/;
const FULL_HS_URL_REGEX = /^http(s)?:\/\/[a-z0-9-]+\.hubspot(qa)?.com/;

/**
 * Check if current path and given URL has matching portal ID
 *
 * @param {String} url
 * @returns {Boolean} true if portal ID matches
 */
export const hasMatchingPortalId = url => {
  const portalIdFromPath = PortalIdParser.getPortalIdFromPath();
  const portalIdFromRef = PortalIdParser.parsePortalIdFromString(url, PORTAL_ID_FROM_PATH_REGEX);
  return portalIdFromRef === portalIdFromPath;
};

/**
 * Validate a redirect URL by checking that it:
 *  - is safe (against XSS)
 *  - has a matching portalId with the current location path
 *  - is either a
 *    - relative URL (ex. /its/so/relative)
 *    - full HubSpot URL (ex. https://app.hubspot.com/path)
 *
 * @param {String} url
 * @returns {Boolean} true if valid app redirect URL
 */
export const isValidAppRedirect = url => {
  if (!url) {
    return false;
  }
  const isRelativeUrl = url.indexOf('/') === 0;
  const isFullHubSpotUrl = FULL_HS_URL_REGEX.test(url);
  return !isUnsafeUrl(url) && hasMatchingPortalId(url) && (isRelativeUrl || isFullHubSpotUrl);
};

// Only http(s) and protocol-relative URLs are permitted as a preview iframe
// src. This blocks executable schemes (ex. `javascript:`, `data:`) that could
// lead to XSS when assigned to an iframe `src`.
const SAFE_PREVIEW_URL_REGEX = /^(https?:)?\/\//i;

/**
 * Validate a preview iframe URL by allowing only `https://`/`http://` and
 * protocol-relative (`//`) URLs, rejecting executable schemes (ex.
 * `javascript:`, `data:`) that could lead to XSS when assigned as an iframe
 * `src`.
 *
 * @param {String} url
 * @returns {Boolean} true if the URL is safe to use as a preview iframe src
 */
export const isSafePreviewUrl = url => {
  return typeof url === 'string' && SAFE_PREVIEW_URL_REGEX.test(url);
};

// Kept identical to the copies in page-creation-ui and page-management-ui.
export const appendQueryParamsToUrl = (baseUrl, queryParams) => {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return baseUrl;
  }
  const url = new URL(baseUrl, window.location.origin);
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.pathname + url.search;
};